import { afterNextRender, Component, ElementRef, inject, Inject, Injector, OnDestroy, ViewChild, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, Validators } from '@angular/forms'
import { disabled, FieldTree, form, min, minLength, required, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ApixHaeVerkkolaskuosoitettaPyynto, ApixVerkkolaskuosoitteenHakutulos, ApixVerkkolaskuosoitteenHakuVastaus } from '@lemon/core-firebase/model/apix'
import {
  KAANNETYN_VEROVELVOLLISUUDEN_LAJIT,
  KULUTTAJAN_VAHIMMAISMAKSUAIKA,
  LaskunAsiakas,
  LaskunAsiakastyyppi,
  LaskunLahetystapa,
  LaskunSahkoinenOsoite,
  LaskunTyypit,
  LaskunTyyppi,
} from '@lemon/core-firebase/model/lasku'
import { YtjToimittaja } from '@lemon/core-firebase/model/web-toimittaja-api'
import { YtjYrityshaunTulos } from '@lemon/core-firebase/model/ytj-api'
import { SahkoisenLaskunValittajaService, Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { TuettuKieli, Valuutta } from '@lemon/core/model/common'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonaid.service'
import { LaskutusprofiiliService } from '../../_angular/service/lasku/laskutusprofiili.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../../_jaettu-angular/_components/are-you-sure.dialog'
import { arvonMuutokset, asetaArvo, nollaa } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { KonfiguraatioPalvelu } from '../../_jaettu-angular/service/lasku/configuration.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../../_jaettu-angular/service/maa.service'
import { AsiakasPoistoVarmistusDialog, AsiakasPoistoVarmistusDialogData } from '../asiakkaat/asiakas-poisto-varmistus.dialog'
import { LaskunAsiakkaanValidointiService, VatTaiYtunnuksePakollisuus } from '../lasku-asiakkaan-validointi.service'
import { annaMaksuaikaVaihtoehdot } from '../maksuaika-vaihtoehdot'
import {
  annaAsiakastyypinOletukset,
  annaOletuslahetystapa,
  annaYtjOsoitteenKaytto,
  AsiakasLomake,
  onkoMyynninLajiSallittu,
  rakennaAsiakasDialoginTulos,
  voikoVerkkolaskuosoitteenHakea,
  voikoVerkkolaskuosoitteenKorvata,
} from './lasku-asiakas-dialog.logic'

export interface LaskuAsiakasDialogData {
  asiakas: LaskunAsiakas | null
  esitaytettyNimi: string
  tyyppi: LaskunTyyppi
  tyyppiMuokattavissa: boolean
  valuutta: string
  kieli: TuettuKieli
  naytaPoista?: boolean
}

export interface LaskuAsiakasDialogTulos {
  nimi: string
  ytunnus: string
  katuosoite: string
  postinro: string
  postitoimipaikka: string
  maa: string
  tyyppi: LaskunTyyppi
  valuutta: string
  kieli: TuettuKieli
  oletusLahetystapa: LaskunLahetystapa
  maksuaikaa: number
  sahkoinenosoite: LaskunSahkoinenOsoite
  laskunVastaanottajat: string[]
  asiakastyyppi: LaskunAsiakastyyppi | null
  laskutusprofiiliAvain: string
}

interface LaskutusprofiiliVaihtoehto {
  avain: string | null
  nimi: string
}

const OLETUSMAKSUAIKA = 14

@Component({
  templateUrl: './lasku-asiakas.dialog.html',
  styleUrls: ['./lasku-asiakas.dialog.css'],
  standalone: false,
})
export class LaskuAsiakasDialog implements OnDestroy {
  @ViewChild('nimiKentta') nimiKentta: ElementRef<HTMLInputElement>

  // eslint-disable-next-line @typescript-eslint/naming-convention
  LaskunLahetystapa = LaskunLahetystapa

  readonly lomake: FieldTree<AsiakasLomake>
  readonly laskunVastaanottajat = signal<string[]>([])

  kielet: BehaviorSubject<TuettuKieli[]> = new BehaviorSubject(['fi', 'en'])
  valuutatObservable: Observable<Valuutta[]>
  tyypitObservable: Observable<LaskunTyyppi[]>
  verkkolaskuoperaattorit: Observable<Verkkolaskuoperaattori[]>
  suodatetutMaat: Observable<Maa[]>
  laskutusprofiilitObservable: Observable<LaskutusprofiiliVaihtoehto[]>
  ytunnusPlaceholderObservable: Observable<string>
  vatTunnuksenValidointitulos = new BehaviorSubject<string>('')
  readonly haetaanVerkkolaskuosoitetta = signal(false)
  readonly verkkolaskuosoitteenHakuEpaonnistui = signal(false)
  readonly loydetytVerkkolaskuosoitteet = signal<ApixVerkkolaskuosoitteenHakutulos[]>([])
  asiakastyypit = LaskunAsiakastyyppi
  readonly asiakastyyppi = signal<LaskunAsiakastyyppi | null>(null)
  readonly naytaTyyppivalinta = signal(false)
  readonly ehdotettuYtjOsoite = signal<YtjToimittaja | null>(null)
  private _ytjstaTaytettyOsoite: YtjToimittaja | null = null
  readonly naytaMaanLukitusohje = signal(false)
  private _maanLukitusohjeenAjastin: ReturnType<typeof setTimeout> | undefined

  private _ngUnsubscribe = new Subject<void>()
  private readonly _ytunnuksenPakollisuus = signal<VatTaiYtunnuksePakollisuus>('ei-pakollinen')
  private readonly _maaPoissa = signal(false)
  private _asiakastyyppiSubject: BehaviorSubject<LaskunAsiakastyyppi | null>
  private _haettuYtunnus: string | null = null
  private _haettuOsoite: string | null = null
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LaskuAsiakasDialogData,
    private _dialogRef: MatDialogRef<LaskuAsiakasDialog, LaskuAsiakasDialogTulos | 'poistettu'>,
    private _dialog: MatDialog,
    private _validationService: FormValidationService,
    private _konfiguraatioPalvelu: KonfiguraatioPalvelu,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _maaService: MaaService,
    private _laskunAsiakkaanValidointiService: LaskunAsiakkaanValidointiService,
    private _codeCheckService: CodeCheckService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _translationService: LemonTranslationService,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {
    const asiakas = data.asiakas
    this.asiakastyyppi.set(asiakas?.asiakastyyppi ?? null)
    this._asiakastyyppiSubject = new BehaviorSubject(this.asiakastyyppi())
    this.naytaTyyppivalinta.set(!asiakas)
    if (this.naytaTyyppivalinta()) {
      this._dialogRef.updateSize('560px')
    }

    this._dialogRef.disableClose = true

    this.valuutatObservable = this._konfiguraatioPalvelu.valuutatObservable
    this.tyypitObservable = combineLatest([this._konfiguraatioPalvelu.tyypitObservable, this._asiakastyyppiSubject]).pipe(
      map(([tyypit, asiakastyyppi]) => tyypit.filter(tyyppi => onkoMyynninLajiSallittu(tyyppi, asiakastyyppi))),
    )

    this.laskunVastaanottajat.set([...(asiakas?.laskunVastaanottajat ?? [])])
    const valittajaTunnus = asiakas?.sahkoinenosoite?.sahkoinenValittaja
    const haettuValittaja = valittajaTunnus ? this._sahkoisenLaskunValittajaService.annaValittaja(valittajaTunnus) : null
    const tallennettuMaksuaika = asiakas?.maksuaikaa ?? OLETUSMAKSUAIKA
    const maksuaikaa = Math.max(tallennettuMaksuaika, this.vahimmaismaksuaika)

    const alkuarvot: AsiakasLomake = {
      nimi: asiakas?.nimi ?? data.esitaytettyNimi ?? '',
      ytunnus: asiakas?.ytunnus ?? '',
      katuosoite: asiakas?.katuosoite ?? '',
      postinro: asiakas?.postinro ?? '',
      postitoimipaikka: asiakas?.postitmp ?? '',
      maa: asiakas?.maa ?? null,
      tyyppi: data.tyyppi,
      valuutta: data.valuutta,
      kieli: data.kieli,
      oletusLahetystapa: annaOletuslahetystapa(asiakas),
      maksuaikaValinta: maksuaikaa,
      maksuaikaa: maksuaikaa,
      verkkolaskuosoite: asiakas?.sahkoinenosoite?.sahkoinenOsoite ?? '',
      verkkolaskuvalittaja: haettuValittaja ?? valittajaTunnus ?? null,
      lisaavastaanottaja: '',
      laskutusprofiiliAvain: asiakas?.laskutusprofiiliAvain ?? null,
    }
    this.lomake = form(signal(alkuarvot), polku => {
      required(polku.nimi)
      minLength(polku.nimi, 2)
      disabled(polku.ytunnus, () => this._ytunnuksenPakollisuus() === 'ei-pakollinen')
      required(polku.ytunnus, { when: () => this._onkoYtunnusPakollinen() })
      validate(polku.ytunnus, ({ value, valueOf }) => this._laskunAsiakkaanValidointiService.annaYtunnuksenVirhe(value(), valueOf(polku.maa), this._ytunnuksenPakollisuus()))
      required(polku.katuosoite)
      required(polku.postinro)
      required(polku.postitoimipaikka)
      required(polku.maa)
      disabled(polku.maa, () => this._maaPoissa())
      required(polku.tyyppi)
      disabled(polku.tyyppi, () => !data.tyyppiMuokattavissa)
      required(polku.valuutta)
      required(polku.kieli)
      required(polku.oletusLahetystapa)
      required(polku.maksuaikaa)
      min(polku.maksuaikaa, () => this.vahimmaismaksuaika)
      required(polku.verkkolaskuosoite, { when: ({ valueOf }) => this._onkoVerkkolaskuPakollinen(valueOf(polku.oletusLahetystapa)) })
      required(polku.verkkolaskuvalittaja, { when: ({ valueOf }) => this._onkoVerkkolaskuPakollinen(valueOf(polku.oletusLahetystapa)) })
      validoiReaktiivisesti(polku.lisaavastaanottaja, this.validateEmail)
    })

    this.laskutusprofiilitObservable = this._laskutusprofiiliService.laskutusprofiiliVaihtoehdotObservable.pipe(
      map(vaihtoehdot =>
        vaihtoehdot.map(vaihtoehto => ({
          avain: vaihtoehto.oletusprofiili ? null : vaihtoehto.avain,
          nimi: vaihtoehto.nimi,
        })),
      ),
    )

    this.laskutusprofiilitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(laskutusprofiilit => {
      if (!laskutusprofiilit.length) {
        return
      }
      const valittuAvain = this.lomake.laskutusprofiiliAvain().value()
      const valittuProfiiliLoytyy = laskutusprofiilit.some(laskutusprofiili => laskutusprofiili.avain === valittuAvain)
      if (!valittuProfiiliLoytyy) {
        asetaArvo(this.lomake.laskutusprofiiliAvain, null)
      }
    })

    this.verkkolaskuoperaattorit = arvonMuutokset(this.lomake.verkkolaskuvalittaja).pipe(
      startWith(this.lomake.verkkolaskuvalittaja().value()),
      map(value => this._filter(value)),
    )

    const tyyppiObservable = arvonMuutokset(this.lomake.tyyppi).pipe(startWith(this.lomake.tyyppi().value()))

    this.suodatetutMaat = combineLatest([tyyppiObservable, this._maaService.kaikkiMaatJaVeroalueetObservable]).pipe(
      map(([tyyppi, maat]) => this._laskunAsiakkaanValidointiService.suodataMaat(maat, tyyppi)),
    )
    combineLatest([tyyppiObservable, this.suodatetutMaat])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([tyyppi, suodatetut]) => this.asetaMaa(tyyppi, suodatetut))

    this.ytunnusPlaceholderObservable = this._laskunAsiakkaanValidointiService.annaYtunnuksenOtsikkoObservable(tyyppiObservable)

    combineLatest([tyyppiObservable, arvonMuutokset(this.lomake.maa).pipe(startWith(this.lomake.maa().value()), distinctUntilChanged()), this._asiakastyyppiSubject])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([tyyppi, maakoodi]) => {
        this._ytunnuksenPakollisuus.set(this._laskunAsiakkaanValidointiService.annaYtunnuksenPakollisuus(tyyppi, maakoodi))
        this._ilmoitaYtunnuksesta()
      })

    arvonMuutokset(this.lomake.maa)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this._ilmoitaYtunnuksesta())

    arvonMuutokset(this.lomake.ytunnus)
      .pipe(
        debounceTime(750),
        map(value => this._laskunAsiakkaanValidointiService.annaViesistaTarkistettavaTunnus(value, this._ytunnuksenPakollisuus())),
        filter(tarkistettava => !!tarkistettava),
        switchMap(tarkistettava => this._laskunAsiakkaanValidointiService.tarkistaAlvNumeroViesista(tarkistettava)),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tulos => this.vatTunnuksenValidointitulos.next(tulos))

    const muokattuYtunnusObservable = arvonMuutokset(this.lomake.ytunnus).pipe(
      debounceTime(500),
      map(value => value?.trim()),
    )
    const lahetystapaObservable = arvonMuutokset(this.lomake.oletusLahetystapa).pipe(startWith(this.lomake.oletusLahetystapa().value()))
    combineLatest([muokattuYtunnusObservable, lahetystapaObservable])
      .pipe(
        filter(([ytunnus, lahetystapa]) => this._voikoVerkkolaskuosoitteenHakea(ytunnus, lahetystapa)),
        tap(([ytunnus]) => this._aloitaVerkkolaskuosoitteenHaku(ytunnus)),
        switchMap(([ytunnus]) => from(this._haeVerkkolaskuosoitteet(ytunnus))),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(osoitteet => this._kaytaHaettujaVerkkolaskuosoitteita(osoitteet))

    arvonMuutokset(this.lomake.ytunnus)
      .pipe(
        map(value => value?.trim()),
        distinctUntilChanged(),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(ytunnus => {
        this.verkkolaskuosoitteenHakuEpaonnistui.set(false)
        this._tyhjennaToisenYtunnuksenVerkkolaskuosoite(ytunnus)
      })

    this._vaihdaMyynninLajiKotimaanMyynniksiJosEiSallittu()
  }

  private async _vaihdaMyynninLajiKotimaanMyynniksiJosEiSallittu() {
    const tyypit = await firstValueFrom(this._konfiguraatioPalvelu.tyypitObservable)
    const tyyppi = this.lomake.tyyppi().value()
    const onkoLajiSallittu = tyypit.some(sallittu => sallittu.avain === tyyppi?.avain)
    const onkoKaannettyVerovelvollisuus = KAANNETYN_VEROVELVOLLISUUDEN_LAJIT.includes(tyyppi?.avain)
    if (!this.lomake.tyyppi().disabled() && !onkoLajiSallittu && onkoKaannettyVerovelvollisuus) {
      asetaArvo(this.lomake.tyyppi, LaskunTyypit.TAVALLINEN)
    }
  }

  private _ilmoitaYtunnuksesta() {
    asetaArvo(this.lomake.ytunnus, this.lomake.ytunnus().value())
  }

  private _onkoYtunnusPakollinen(): boolean {
    const pakollisuus = this._ytunnuksenPakollisuus()
    const onkoTyypinMukaanPakollinen = pakollisuus === 'pakollinen-ytunnus' || pakollisuus === 'pakollinen-vat'
    const onkoUudenYrityksenTunnus = this.onkoUusiYritysasiakas && pakollisuus !== 'ei-pakollinen'
    return onkoTyypinMukaanPakollinen || onkoUudenYrityksenTunnus
  }

  private _onkoVerkkolaskuPakollinen(lahetystapa: LaskunLahetystapa): boolean {
    return this.onkoUusiYritysasiakas && lahetystapa === LaskunLahetystapa.SAHKOINEN
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string, vainKosketettu = true): boolean {
    const naytetaanko = !vainKosketettu || kentta().dirty() || kentta().touched()
    const onkoVirhe = kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
    return naytetaanko && onkoVirhe
  }

  valitseLahetystapa(lahetystapa: LaskunLahetystapa) {
    asetaArvo(this.lomake.oletusLahetystapa, lahetystapa)
  }

  private _tyhjennaToisenYtunnuksenVerkkolaskuosoite(ytunnus: string) {
    const onkoOsoiteHaettuToisellaYtunnuksella = this.onkoVerkkolaskuosoiteHaettu && ytunnus !== this._haettuYtunnus
    if (!onkoOsoiteHaettuToisellaYtunnuksella) {
      return
    }
    asetaArvo(this.lomake.verkkolaskuosoite, '')
    asetaArvo(this.lomake.verkkolaskuvalittaja, null)
    this.loydetytVerkkolaskuosoitteet.set([])
    this._haettuOsoite = null
    this._haettuYtunnus = null
  }

  kaytaValittuaYtjYritysta(yritys: YtjYrityshaunTulos) {
    this._taytaYrityksenNimiJaYtunnus(yritys)
    this.ehdotettuYtjOsoite.set(null)
  }

  naytaMaanLukitusohjeHetkeksi() {
    if (!this.lomake.maa().disabled()) {
      return
    }
    this.naytaMaanLukitusohje.set(true)
    clearTimeout(this._maanLukitusohjeenAjastin)
    this._maanLukitusohjeenAjastin = setTimeout(() => {
      this.naytaMaanLukitusohje.set(false)
    }, 3200)
  }

  kaytaEhdotettuaYtjOsoitetta() {
    this._taytaYrityksenOsoite(this.ehdotettuYtjOsoite())
    this.ehdotettuYtjOsoite.set(null)
  }

  kaytaYtjOsoitetta(osoite: YtjToimittaja) {
    const lomakkeenOsoite = { katuosoite: this.lomake.katuosoite().value(), postinro: this.lomake.postinro().value(), postitoimipaikka: this.lomake.postitoimipaikka().value() }
    const kaytto = annaYtjOsoitteenKaytto(lomakkeenOsoite, this._ytjstaTaytettyOsoite, osoite)
    if (kaytto === 'tayta') {
      this._taytaYrityksenOsoite(osoite)
    } else if (kaytto === 'ehdota') {
      this.ehdotettuYtjOsoite.set(osoite)
    }
  }

  private _taytaYrityksenNimiJaYtunnus(yritys: YtjYrityshaunTulos) {
    asetaArvo(this.lomake.nimi, yritys.nimi ?? '')
    asetaArvo(this.lomake.ytunnus, yritys.ytunnus ?? '')
    this.lomake().markAsDirty()
  }

  private _taytaYrityksenOsoite(osoite: YtjToimittaja) {
    asetaArvo(this.lomake.katuosoite, osoite.katuosoite ?? '')
    asetaArvo(this.lomake.postinro, osoite.postinro ?? '')
    asetaArvo(this.lomake.postitoimipaikka, osoite.postitmp ?? '')
    this._ytjstaTaytettyOsoite = osoite
  }

  get onkoUusiYritysasiakas(): boolean {
    return !this.data.asiakas && this.asiakastyyppi() === LaskunAsiakastyyppi.YRITYS
  }

  get otsikonAvain(): string {
    if (this.data.asiakas) {
      return 'lasku.asiakas-dialog.muokkaa-otsikko'
    }
    return this.asiakastyyppi() === LaskunAsiakastyyppi.HENKILO ? 'lasku.asiakas-dialog.uusi-yksityisasiakas' : 'lasku.asiakas-dialog.uusi-yritysasiakas'
  }

  get nimenOtsikonAvain(): string {
    if (this.asiakastyyppi() === LaskunAsiakastyyppi.YRITYS) {
      return 'lasku.asiakas-dialog.yrityksen-nimi'
    }
    if (this.asiakastyyppi() === LaskunAsiakastyyppi.HENKILO) {
      return 'lasku.asiakas-dialog.nimi'
    }
    return 'lasku.nimi'
  }

  get nimenPaikkamerkinAvain(): string {
    return this.asiakastyyppi() === LaskunAsiakastyyppi.HENKILO ? 'lasku.asiakas-dialog.nimi-paikkamerkki-henkilo' : 'lasku.asiakas-dialog.yrityksen-nimi'
  }

  get onkoVerkkolaskuosoiteHaettu(): boolean {
    return !!this._haettuOsoite && this.lomake.verkkolaskuosoite().value()?.trim() === this._haettuOsoite
  }

  valitseAsiakastyyppi(asiakastyyppi: LaskunAsiakastyyppi) {
    this.asiakastyyppi.set(asiakastyyppi)
    this.naytaTyyppivalinta.set(false)
    this._dialogRef.updateSize('880px')
    this._asetaAsiakastyypinOletukset(asiakastyyppi)
    this._asiakastyyppiSubject.next(asiakastyyppi)
    if (asiakastyyppi === LaskunAsiakastyyppi.HENKILO) {
      afterNextRender(() => this.nimiKentta?.nativeElement.focus(), { injector: this._injector })
    }
  }

  async pyydaSulkemista() {
    if (!this.lomake().dirty()) {
      this._dialogRef.close()
      return
    }
    const onkoOlemassaOlevaAsiakas = !!this.data.asiakas
    const data: AreYouSureDialogData = {
      header: this._translationService.lokalisoi(onkoOlemassaOlevaAsiakas ? 'lasku.asiakas-dialog.hylkaa-muutokset-otsikko' : 'lasku.asiakas-dialog.vaihda-tyyppi-otsikko'),
      text: this._translationService.lokalisoi(onkoOlemassaOlevaAsiakas ? 'lasku.asiakas-dialog.hylkaa-muutokset-teksti' : 'lasku.asiakas-dialog.hylkaa-uusi-asiakas-teksti'),
      rightAction: this._translationService.lokalisoi(onkoOlemassaOlevaAsiakas ? 'lasku.asiakas-dialog.hylkaa-muutokset' : 'lasku.asiakas-dialog.hylkaa-tiedot'),
    }
    const hylataan = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (hylataan) {
      this._dialogRef.close()
    }
  }

  async vaihdaAsiakastyyppia() {
    if (this.lomake().dirty()) {
      const data: AreYouSureDialogData = {
        header: this._translationService.lokalisoi('lasku.asiakas-dialog.vaihda-tyyppi-otsikko'),
        text: this._translationService.lokalisoi('lasku.asiakas-dialog.vaihda-tyyppi-teksti'),
        rightAction: this._translationService.lokalisoi('lasku.asiakas-dialog.hylkaa-tiedot'),
      }
      const hylataan = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
      if (!hylataan) {
        return
      }
    }
    nollaa(this.lomake, {
      nimi: this.data.esitaytettyNimi ?? '',
      ytunnus: '',
      katuosoite: '',
      postinro: '',
      postitoimipaikka: '',
      maa: null,
      tyyppi: this.data.tyyppi,
      valuutta: this.data.valuutta,
      kieli: this.data.kieli,
      oletusLahetystapa: LaskunLahetystapa.SAHKOPOSTI,
      maksuaikaValinta: OLETUSMAKSUAIKA,
      maksuaikaa: OLETUSMAKSUAIKA,
      verkkolaskuosoite: '',
      verkkolaskuvalittaja: null,
      lisaavastaanottaja: '',
      laskutusprofiiliAvain: null,
    })
    this.laskunVastaanottajat.set([])
    this.loydetytVerkkolaskuosoitteet.set([])
    this._haettuYtunnus = null
    this._haettuOsoite = null
    this.ehdotettuYtjOsoite.set(null)
    this._ytjstaTaytettyOsoite = null
    this.asiakastyyppi.set(null)
    this._asiakastyyppiSubject.next(null)
    this.naytaTyyppivalinta.set(true)
    this._dialogRef.updateSize('560px')
  }

  private _asetaAsiakastyypinOletukset(asiakastyyppi: LaskunAsiakastyyppi) {
    const nykyiset = {
      maksuaikaValinta: this.lomake.maksuaikaValinta().value(),
      maksuaikaa: this.lomake.maksuaikaa().value(),
      tyyppi: this.lomake.tyyppi().value(),
      oletusLahetystapa: this.lomake.oletusLahetystapa().value(),
    }
    const oletukset = annaAsiakastyypinOletukset(asiakastyyppi, nykyiset, !this.lomake.tyyppi().disabled())
    if (oletukset.maksuaikaValinta !== undefined) {
      asetaArvo(this.lomake.maksuaikaValinta, oletukset.maksuaikaValinta)
    }
    if (oletukset.maksuaikaa !== undefined) {
      asetaArvo(this.lomake.maksuaikaa, oletukset.maksuaikaa)
    }
    if (oletukset.tyyppi !== undefined) {
      asetaArvo(this.lomake.tyyppi, oletukset.tyyppi)
    }
    if (oletukset.oletusLahetystapa !== undefined) {
      asetaArvo(this.lomake.oletusLahetystapa, oletukset.oletusLahetystapa)
    }
  }

  valitseVerkkolaskuosoite(osoite: ApixVerkkolaskuosoitteenHakutulos) {
    asetaArvo(this.lomake.verkkolaskuosoite, osoite.sahkoinenOsoite ?? '')
    const valittaja = osoite.sahkoinenValittaja ? this._sahkoisenLaskunValittajaService.annaValittaja(osoite.sahkoinenValittaja) : null
    asetaArvo(this.lomake.verkkolaskuvalittaja, valittaja ?? osoite.sahkoinenValittaja)
    this._haettuOsoite = osoite.sahkoinenOsoite
  }

  private _voikoVerkkolaskuosoitteenHakea(ytunnus: string, lahetystapa: LaskunLahetystapa): boolean {
    return voikoVerkkolaskuosoitteenHakea({
      ytunnus,
      lahetystapa,
      onkoYtunnusKelvollinen: !!ytunnus && this._codeCheckService.isValidYTunnus(ytunnus),
      haettuYtunnus: this._haettuYtunnus,
      nykyinenOsoite: this.lomake.verkkolaskuosoite().value(),
      haettuOsoite: this._haettuOsoite,
    })
  }

  private _voikoOsoitteenKorvata(): boolean {
    return voikoVerkkolaskuosoitteenKorvata(this.lomake.verkkolaskuosoite().value(), this._haettuOsoite)
  }

  async haeVerkkolaskuosoiteUudelleen() {
    const ytunnus = this.lomake.ytunnus().value()?.trim()
    this._aloitaVerkkolaskuosoitteenHaku(ytunnus)
    const osoitteet = await this._haeVerkkolaskuosoitteet(ytunnus)
    this._kaytaHaettujaVerkkolaskuosoitteita(osoitteet)
  }

  private _aloitaVerkkolaskuosoitteenHaku(ytunnus: string) {
    this._haettuYtunnus = ytunnus
    this.haetaanVerkkolaskuosoitetta.set(true)
    this.verkkolaskuosoitteenHakuEpaonnistui.set(false)
  }

  private async _haeVerkkolaskuosoitteet(ytunnus: string): Promise<ApixVerkkolaskuosoitteenHakutulos[] | null> {
    try {
      const pyynto: ApixHaeVerkkolaskuosoitettaPyynto = { ytunnus }
      const vastaus = await this._firebaseLemonaid.functionsCall<ApixHaeVerkkolaskuosoitettaPyynto, ApixVerkkolaskuosoitteenHakuVastaus>('apixHaeVerkkolaskuosoite', pyynto, undefined, 'europe-north1')
      return vastaus?.osoitteet ?? []
    } catch (err) {
      console.error('Verkkolaskuosoitteen haku epäonnistui', err)
      return null
    }
  }

  private _kaytaHaettujaVerkkolaskuosoitteita(osoitteet: ApixVerkkolaskuosoitteenHakutulos[] | null) {
    this.haetaanVerkkolaskuosoitetta.set(false)
    const onkoYtunnusYhaSama = this.lomake.ytunnus().value()?.trim() === this._haettuYtunnus
    if (!onkoYtunnusYhaSama) {
      return
    }
    const hakuEpaonnistui = osoitteet === null
    if (hakuEpaonnistui) {
      this.verkkolaskuosoitteenHakuEpaonnistui.set(true)
      this._haettuYtunnus = null
      return
    }
    this.loydetytVerkkolaskuosoitteet.set(osoitteet)
    if (!this._voikoOsoitteenKorvata()) {
      return
    }
    const ensimmainen = osoitteet[0]
    if (ensimmainen) {
      this.valitseVerkkolaskuosoite(ensimmainen)
    } else {
      asetaArvo(this.lomake.verkkolaskuosoite, '')
      asetaArvo(this.lomake.verkkolaskuvalittaja, null)
      this._haettuOsoite = null
    }
  }

  ngOnDestroy() {
    clearTimeout(this._maanLukitusohjeenAjastin)
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private _filter(value: string | Verkkolaskuoperaattori): Verkkolaskuoperaattori[] {
    if (value === '' || !value) {
      return []
    }
    let filterValue = ''
    if (value instanceof String || typeof value === 'string') {
      filterValue = value.toLowerCase()
    } else {
      filterValue = value && value.nimi ? value.nimi.toLowerCase() : ''
    }
    return this._sahkoisenLaskunValittajaService
      .annaKaikkiValittajat()
      .filter(operaattori => operaattori.nimi.toLowerCase().includes(filterValue) || operaattori.tunnus.toLowerCase().includes(filterValue))
  }

  valittajaDisplayFn(operaattori?: Verkkolaskuoperaattori): string | undefined {
    if (operaattori && operaattori.nimi) {
      return operaattori.nimi + ' (' + operaattori.tunnus + ')'
    }
    if (operaattori instanceof String || typeof operaattori === 'string') {
      return operaattori as any as string
    }
    return undefined
  }

  vertaaTyyppia = (o1: LaskunTyyppi, o2: LaskunTyyppi): boolean => {
    if (o1 && o2) {
      return o1.avain === o2.avain
    }
    return false
  }

  validateEmail = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    return Validators.email(ctrl)
  }

  lisaaVastaanottaja() {
    this.lisaaEmail(this.lomake.lisaavastaanottaja)
  }

  private lisaaEmail(kentta: FieldTree<string>) {
    if (!kentta().valid()) {
      return
    }
    const syotetty = kentta().value()
    if ((syotetty || '').trim()) {
      let arvo = this._validationService.processValue(syotetty.trim())
      arvo = arvo.replace(/,/g, '').trim()
      this.laskunVastaanottajat.update(vastaanottajat => [...vastaanottajat, arvo])
    }
    asetaArvo(kentta, '')
  }

  poistaVastaanottaja(vastaanottaja: string) {
    this.laskunVastaanottajat.update(vastaanottajat => {
      const indeksi = vastaanottajat.indexOf(vastaanottaja)
      return indeksi >= 0 ? vastaanottajat.filter((_, i) => i !== indeksi) : vastaanottajat
    })
  }

  async poistaAsiakas() {
    const data: AsiakasPoistoVarmistusDialogData = { asiakas: this.data.asiakas }
    const poistettu = await firstValueFrom(this._dialog.open(AsiakasPoistoVarmistusDialog, { data }).afterClosed())
    if (poistettu) {
      this._dialogRef.close('poistettu')
    }
  }

  tallenna() {
    this.lisaaVastaanottaja()
    const virheitaLomakkeella = this._validationService.onkoLomakkeessaVirheita(this.lomake)
    if (virheitaLomakkeella) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    this._dialogRef.close(rakennaAsiakasDialoginTulos(this.lomake().value(), this.laskunVastaanottajat(), this.asiakastyyppi()))
  }

  private asetaMaa(tyyppi: LaskunTyyppi, suodatetutMaat: Maa[]) {
    if (this._laskunAsiakkaanValidointiService.onkoKotimaanLasku(tyyppi)) {
      asetaArvo(this.lomake.maa, 'FIN')
      this._maaPoissa.set(true)
      this._ilmoitaMaasta()
    } else {
      this._maaPoissa.set(false)
      this._ilmoitaMaasta()
      if (suodatetutMaat.findIndex(maa => maa.koodi === this.lomake.maa().value()) < 0) {
        asetaArvo(this.lomake.maa, null)
      }
    }
  }

  private _ilmoitaMaasta() {
    asetaArvo(this.lomake.maa, this.lomake.maa().value())
  }

  get vahimmaismaksuaika(): number {
    return this.asiakastyyppi() === LaskunAsiakastyyppi.HENKILO ? KULUTTAJAN_VAHIMMAISMAKSUAIKA : 1
  }

  get maksuaikaVaihtoehdot(): number[] {
    return annaMaksuaikaVaihtoehdot(this.lomake.maksuaikaa().value(), this.vahimmaismaksuaika)
  }
}
